import { Button } from 'components/ui/button';
import { buttonStyle } from 'components/buttons/buttonStyle';
import { PriorityHighRounded as PriorityHighRoundedIcon } from 'components/icons';
import { Alert, AlertDescription } from 'components/ui/alert';
import { usePostHog } from '@posthog/react';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { logError } from 'utils/monitoring';
import { officeInactiveSuffix } from 'utils/officeOptionRenderer';

// Types
import {
    InvoiceDebtReminderDto,
    InvoiceDebtReminderEmailForm,
    InvoiceDebtReminderScopeType,
    InvoiceDebtReminderType,
    InvoiceReminderIntervalDto,
} from 'types/invoices';

// Components
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput';
import CheckboxListInput from 'components/forms/inputs/rhf-inputs/CheckboxListInput';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import InfoTooltip from 'components/InfoTooltip';
import Loader from 'components/Loader';
import EmployeePickerModal from 'components/modals/employees/EmployeePickerModal';
import { useActiveEmployees } from 'components/modals/employees/useActiveEmployees.ts';
import { ModalHeader } from 'components/modals/ModalHeader';
import { ModalFooterSimple } from '../ModalFooterSimple';
import { useEmailManagement } from './hooks/useEmailManagement';
import { EmailFieldsSection } from './shared/EmailFieldsSection';
import { OfficeEmailSelector } from './shared/OfficeEmailSelector';
import { SubjectInput } from './shared/SubjectInput';
import { TemplateLanguageSelector } from './shared/TemplateLanguageSelector';

// Store
import { useDispatch } from 'store';
import {
    useGetAvailableLanguagesQuery,
    useGetDebtInvoiceEmailTemplateQuery,
    useSendDebtInvoiceReminderMessageMutation,
} from 'store/api/emails';
import { useGetAllEmployeesQuery } from 'store/api/employees';
import { useGetOfficesWithFiltersQuery } from 'store/api/offices.ts';
import { clearDebtReminderInvoices } from 'store/reducers/invoices';

// Util
import { errorAlert, successAlert } from 'utils/alert';
import { getDebtReminderTemplateType } from 'utils/debtReminderUtils';
import { downloadFile } from 'utils/downloadFile';
import { buildHtmlForMultilineText } from 'utils/emails';
import { getClassifierText, t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers.ts';

// Hooks
import useAuth from 'hooks/useAuth';

export const FORM_OVERFLOW_CUSTOM_STYLE = {
    display: 'grid',
    gridTemplateRows: 'auto 1fr auto',
    width: '1109px',
};

interface Props {
    initialData: Omit<InvoiceDebtReminderEmailForm, 'subject' | 'content' | 'includePdfs'>;
    invoices: InvoiceDebtReminderDto[];
    debtReminderType: InvoiceDebtReminderType;
    debtReminderScopeType: InvoiceDebtReminderScopeType;
    debtCollectionReminderIntervals: InvoiceReminderIntervalDto[];
    onClose: () => void;
}

export const DebtReminderSendFormModal = (props: Props) => {
    const [fileUploadBlocked] = useState(false);
    const { onClose, initialData, invoices, debtCollectionReminderIntervals } = props;
    const posthog = usePostHog();
    const dispatch = useDispatch();

    const { user } = useAuth();
    const { data: employees = [] } = useGetAllEmployeesQuery();
    const employeeOptions = useActiveEmployees(employees);
    const [sendDebtInvoiceReminderMessage] = useSendDebtInvoiceReminderMessageMutation();

    const { classifiers } = useClassifiers({ codes: ['LANGUAGE'] });

    const { data: officesData } = useGetOfficesWithFiltersQuery({
        filters: 'invoicingEmailAddress!=null;invoicingEmailAddress!=""',
        pageSize: 1000,
    });
    const offices = officesData?.content ?? [];

    // Map offices to NamedEntityMinWithEmailDto
    const officeOptions = offices.map((office) => ({
        id: office.id,
        name: `${office.name}${officeInactiveSuffix(office) ?? ''}`,
        email: office.invoicingEmailAddress,
    }));

    // Create "Me" option for current user
    const meOption = user?.workEmailAddress
        ? {
              id: -1,
              name: 'Me',
              email: user.workEmailAddress,
          }
        : null;

    // Prepend "Me" option to office options
    const emailSenderOptions = meOption ? [meOption, ...officeOptions] : officeOptions;

    const { handleSubmit, control, setValue, getValues } = useForm<InvoiceDebtReminderEmailForm>({
        mode: 'onChange',
        defaultValues: {
            ...initialData,
            includePdfs: true,
            includePenaltyPdfs: true,
            additionalDebtReminderText: '',
            invoicedByOffice:
                invoices.length === 1 && invoices[0].invoicedByOffice?.invoicingEmailAddress
                    ? {
                          id: invoices[0].invoicedByOffice.id,
                          name: invoices[0].invoicedByOffice.name,
                          email: invoices[0].invoicedByOffice.invoicingEmailAddress,
                      }
                    : undefined,
        },
    });

    const {
        employeePickerOpen,
        setEmployeePickerOpen,
        removeEmail,
        addEmail,
        openEmployeePicker,
        handleEmployeePickerConfirm,
    } = useEmailManagement({ setValue, getValues, employees });

    const [to, cc, bcc, replyTo, customEmailTemplateLanguage, selectedInvoices, includePdfs] = useWatch({
        control,
        name: ['to', 'cc', 'bcc', 'replyTo', 'customEmailTemplateLanguage', 'selectedInvoices', 'includePdfs'],
    });

    const noInvoicesSelected = !selectedInvoices || selectedInvoices.length === 0;

    const { data: templateData, isLoading: emailTemplateLoading } = useGetDebtInvoiceEmailTemplateQuery({
        invoiceIds:
            selectedInvoices && selectedInvoices.length > 0
                ? selectedInvoices.join(',')
                : props.invoices.map((invoice) => invoice.id).join(','),
        langCode: customEmailTemplateLanguage?.value,
        debtReminderType: props.debtReminderType,
        debtReminderScopeType: props.debtReminderScopeType,
    });

    const templateType = getDebtReminderTemplateType(props.debtReminderType, props.debtReminderScopeType);

    const { data: availableLanguages } = useGetAvailableLanguagesQuery(templateType);

    const reminderInterval = debtCollectionReminderIntervals.find(
        (interval) => interval.debtReminderType === props.debtReminderType
    );

    const shouldIncludePenaltyCalculations =
        (props.debtReminderType === InvoiceDebtReminderType.TYPE3 ||
            props.debtReminderType === InvoiceDebtReminderType.TYPE4) &&
        (reminderInterval?.isPenaltyCalculationTriggered || false);

    const activeInvoices =
        selectedInvoices && selectedInvoices.length > 0
            ? invoices.filter((inv) => selectedInvoices.includes(inv.id))
            : invoices;

    const penaltyCalculationAttachments = shouldIncludePenaltyCalculations
        ? activeInvoices.map((invoice) => {
              const today = format(new Date(), 'yyyy-MM-dd');
              return {
                  invoiceId: invoice.id,
                  invoiceNumber: invoice.invoiceNumber,
                  filename: `${invoice.invoiceNumber}_late_payment_${today}.pdf`,
              };
          })
        : [];

    const handlePenaltyCalculationClick = (invoiceId: number, filename: string) => {
        const language = customEmailTemplateLanguage?.value;
        downloadFile(
            `/v1/invoices/${invoiceId}/debt-reminder-penalty-pdf?reminderType=${props.debtReminderType}&language=${language}`,
            filename
        );
    };

    // TODO(react-doctor: no-cascading-set-state): RHF setValue calls seeding
    // independent form fields from async template data — framework pattern.
    useEffect(() => {
        const templateLanguage = getClassifierText(classifiers['LANGUAGE'], templateData?.langCode);
        setValue('subject', templateData?.subject || '');
        setValue('content', templateData?.content || '');
        setValue('customEmailTemplateLanguage', {
            label: templateLanguage || '',
            value: templateData?.langCode || '',
        });
        // setValue('signature', templateData?.signature || '');
    }, [classifiers, setValue, templateData]);

    const handleOnChange = async (data: InvoiceDebtReminderEmailForm) => {
        if (fileUploadBlocked) {
            return;
        }

        try {
            await sendDebtInvoiceReminderMessage({
                to: data.to,
                cc: data.cc,
                bcc: data.bcc,
                replyTo: data.replyTo,
                subject: data.subject,
                content: buildHtmlForMultilineText(data.content),
                // Send email directly for "Me" option (id === -1), otherwise send Office ID
                invoicedByOfficeId: data.invoicedByOffice?.id === -1 ? undefined : data.invoicedByOffice?.id,
                invoicingEmailAddress:
                    data.invoicedByOffice?.id === -1 ? data.invoicedByOffice?.email || undefined : undefined,
                invoiceIds: selectedInvoices,
                langCode: data.customEmailTemplateLanguage?.value,
                includePdfs: includePdfs,
                includePenaltyPdfs: data.includePenaltyPdfs,
                debtReminderType: props.debtReminderType,
                debtReminderScopeType: props.debtReminderScopeType,
                additionalDebtReminderText: data.additionalDebtReminderText,
            }).unwrap();
            posthog.capture('debt_reminder_sent', {
                scope: props.debtReminderScopeType === InvoiceDebtReminderScopeType.SINGLE_INVOICE ? 'single' : 'bulk',
            });
            // Unselect the invoice from the table after successful send
            dispatch(clearDebtReminderInvoices());
            successAlert(t('invoices.debt-reminder-sent-success'));
            onClose();
        } catch (error) {
            console.error('Failed to send debt reminder:', error);
            errorAlert(t('invoices.debt-reminder-sent-error'));
            logError(error);
        }
    };

    if (emailTemplateLoading) {
        return <Loader />;
    }

    return (
        <>
            <ModalHeader title={t('invoices.debt-reminder-modal.title')} onCancel={onClose} />
            <div className="h-full overflow-y-auto p-6 max-sm:px-4">
                {fileUploadBlocked ? (
                    <Alert className="border-error bg-error-light mb-6">
                        <PriorityHighRoundedIcon className="text-error size-4" />
                        <AlertDescription className="text-foreground">
                            <FormattedMessage id="invoices.summary-sidebar.file-upload-max-size" />
                        </AlertDescription>
                    </Alert>
                ) : null}
                <div className="flex flex-row gap-6">
                    <div className="min-w-0 grow basis-5/12">
                        <OfficeEmailSelector
                            control={control}
                            setValue={setValue}
                            getValues={getValues}
                            options={emailSenderOptions}
                        />

                        <EmailFieldsSection
                            control={control}
                            to={to}
                            cc={cc}
                            bcc={bcc}
                            replyTo={replyTo}
                            onRemoveEmail={removeEmail}
                            onAddEmail={addEmail}
                            onOpenEmployeePicker={openEmployeePicker}
                        />

                        <TemplateLanguageSelector
                            control={control}
                            setValue={setValue}
                            languageClassifier={classifiers['LANGUAGE']}
                            availableLanguages={availableLanguages}
                            preferredLanguage={invoices.length === 1 ? invoices[0].invoicePdfLanguage : undefined}
                        />

                        <CheckboxInput
                            control={control}
                            name="includePdfs"
                            label="invoices.debt-reminder-modal.include-pdfs"
                            disabled={false}
                            tooltip={<InfoTooltip text="invoices.debt-reminder-modal.include-pdfs-tooltip" />}
                        />

                        {penaltyCalculationAttachments.length > 0 && (
                            <div className="my-4">
                                <CheckboxInput
                                    control={control}
                                    name="includePenaltyPdfs"
                                    label="invoices.debt-reminder-modal.include-penalty-pdfs"
                                    disabled={false}
                                    tooltip={
                                        <InfoTooltip text="invoices.debt-reminder-modal.include-penalty-pdfs-tooltip" />
                                    }
                                />
                                <div className="mt-2 flex flex-col gap-1">
                                    {penaltyCalculationAttachments.map((attachment) => (
                                        <button
                                            key={attachment.invoiceId}
                                            type="button"
                                            className="text-link text-body1 cursor-pointer text-left"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handlePenaltyCalculationClick(
                                                    attachment.invoiceId,
                                                    attachment.filename
                                                );
                                            }}
                                        >
                                            {attachment.filename}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <CheckboxListInput
                            name="selectedInvoices"
                            control={control}
                            label="invoices.debt-reminder-modal.select-invoices"
                            options={invoices.map((invoice) => ({
                                value: invoice.id,
                                label: invoice.invoiceNumber,
                            }))}
                            wrapperClassName="py-2"
                            tooltip={<InfoTooltip text="invoices.debt-reminder-modal.select-invoices-tooltip" />}
                            labelLink={(value) => `/invoice/${value}`}
                        />
                    </div>
                    <div className="min-w-0 grow basis-7/12">
                        <SubjectInput control={control} label="invoices.email.email-subject" />

                        <div className="mt-4">
                            <TextAreaInput
                                name="content"
                                control={control}
                                label="invoices.email.email-content"
                                minRows={13}
                                maxRows={13}
                                required
                            />
                        </div>

                        {templateData?.signature && (
                            <div className="mt-4">
                                {/* Server-generated signature template — content is backend-controlled, not user input. */}
                                {/* oxlint-disable-next-line react/no-danger */}
                                <div className="mt-2" dangerouslySetInnerHTML={{ __html: templateData.signature }} />
                            </div>
                        )}

                        <div className="mt-4">
                            <TextAreaInput
                                name="additionalDebtReminderText"
                                control={control}
                                label="invoices.debt-reminder-modal.additional-debt-notes"
                                placeholder="invoices.debt-reminder-modal.additional-debt-notes-placeholder"
                                minRows={3}
                                maxRows={5}
                                required={false}
                                tooltip={
                                    <InfoTooltip text="invoices.debt-reminder-modal.additional-debt-notes-tooltip" />
                                }
                            />
                        </div>
                    </div>
                </div>
            </div>
            <ModalFooterSimple
                tooltips={
                    noInvoicesSelected
                        ? { 'invoice-send': t('invoices.debt-reminder-modal.no-invoices-selected-tooltip') }
                        : undefined
                }
                buttons={[
                    <Button {...buttonStyle({ variant: 'outlined', color: 'primary' })} key="cancel" onClick={onClose}>
                        {t('cancel')}
                    </Button>,
                    <Button
                        {...buttonStyle({ variant: 'contained', color: 'secondary' })}
                        key="invoice-send"
                        onClick={() => {
                            void handleSubmit(handleOnChange)();
                        }}
                        disabled={fileUploadBlocked || noInvoicesSelected}
                    >
                        {t('invoices.form.send')}
                    </Button>,
                ]}
            />
            <EmployeePickerModal
                open={employeePickerOpen}
                onClose={() => setEmployeePickerOpen(false)}
                onConfirm={handleEmployeePickerConfirm}
                title="forms.select-employees"
                employeeOptions={employeeOptions}
            />
        </>
    );
};
